import { useState, useEffect } from 'react';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { zodResolver } from '@hookform/resolvers/zod';
import service from '@Service';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@Utils/cn';
import { Edit, Plus } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import AsyncSelect from 'react-select/async';
import { ensurePhonePrefix } from '../../utils';

const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[a-z]/, 'Password must contain a lowercase letter')
  .regex(/[A-Z]/, 'Password must contain an uppercase letter')
  .regex(/[0-9]/, 'Password must contain a number')
  .regex(/[^a-zA-Z0-9]/, 'Password must contain a special character');

const DEFAULT_PASSWORD_TOOL_TIP = [
  'Password must be at least 8 characters',
  'Password must contain a lowercase letter',
  'Password must contain an uppercase letter',
  'Password must contain a number',
  'Password must contain a special character',
];

type SelectOption = {
  value: string;
  label: string;
};

const SelectOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
});

const phoneNumberSchema = z
  .string()
  .transform((v) => v.replace(/\D/g, ''))
  .refine((v) => v.length > 0, {
    message: 'Phone number is required',
  })
  .refine((v) => /^\+?1?\d{10}$/.test(v), {
    message: 'Please enter a valid phone number',
  });

const UserSchema = z.object({
  name: z.string().min(2, 'Please enter at least 2 characters'),
  email: z.string().min(2, 'Please enter a valid email'),
  password: passwordSchema,
  phoneNumber: phoneNumberSchema,
  roles: z.array(SelectOptionSchema),
});

export type UserFormValues = z.infer<typeof UserSchema>;

function UserForm({
  edit = false,
  data = null,
}: {
  edit?: boolean;
  data?: UserFormValues | null;
}) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const query = useQueryClient();

  const searchAllUserRoles = async (search) => {
    try {
      if (!search) return;
      const query = {
        'filter[name]': `*${search}*`,
      };

      const userRoles = await service.getUserRoles(query);
      return userRoles?.data;
    } catch (error) {
      console.error('Failed to fetch userRoles:', error);
      return [];
    }
  };

  const loadOptions = async (search: string): Promise<SelectOption[]> => {
    const res = await searchAllUserRoles(search);
    const dataArray = res.map((role) => {
      return {
        value: role.id,
        label: role.name,
      };
    });

    return dataArray;
  };

  const form = useForm<UserFormValues>({
    resolver: zodResolver(UserSchema),
    defaultValues: {
      name: data?.name || '',
      email: data?.email || '',
      password: '',
      phoneNumber: data?.phoneNumber || '',
      roles: data?.roles || [],
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const { handleSubmit, control, reset } = form;

  const customStyles = {
    control: (styles) => ({
      ...styles,
      borderRadius: '8px',
    }),
  };

  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);

    const area = digits.slice(0, 3);
    const middle = digits.slice(3, 6);
    const last = digits.slice(6, 10);

    if (digits.length <= 3) return area;
    if (digits.length <= 6) return `(${area}) ${middle}`;
    return `(${area}) ${middle}-${last}`;
  };

  const onSubmit = async (values: UserFormValues) => {
    const { name, email, password, phoneNumber, roles } = values;

    const roleIds = roles.map((role) => role.value);
    const formattedPhone = ensurePhonePrefix(phoneNumber);

    const userObj = {
      name,
      email: email.toLowerCase(),
      password,
      phoneNumber: formattedPhone,
      roles: roleIds ?? [],
    };
    if (edit) {
      // const updatedRole = await service.updateUser(userObj);
      // if (!updatedRole) {
      //   toastError('Error updating user');
      // } else {
      //   query.invalidateQueries({ queryKey: ['users'] });
      //   setSheetOpen(false);
      //   toastSuccess('User updated successfully');
      // }
    } else {
      const newUser = await service.createUser(userObj);

      if (newUser?.code === 400) {
        toastError(`Error: ${newUser.message}`);

        if (newUser?.details?.field === 'Email') {
          form.setError('email', { message: newUser.message });
        } else if (newUser?.details?.field === 'PhoneNumber') {
          form.setError('phoneNumber', { message: newUser.message });
        }
      } else {
        query.invalidateQueries({ queryKey: ['users'] });
        setSheetOpen(false);
        toastSuccess('User created successfully');
      }
    }
  };

  useEffect(() => {
    if (data) {
      reset({
        name: data?.name || '',
        email: data?.email || '',
        password: '',
        phoneNumber: data?.phoneNumber || '',
        roles: data.roles || [],
      });
    } else {
      reset({
        name: '',
        email: '',
        password: '',
        phoneNumber: '',
        roles: [],
      });
    }
  }, [data, sheetOpen, reset]);

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            icon={{
              src: edit ? <Edit /> : <Plus />,
            }}
            className={cn('justify-start')}
          >
            {edit ? 'Edit' : 'Create new'} user
          </Button>
        </SheetTrigger>
        <SheetContent className="gap-0">
          <Form {...form}>
            <form
              onClick={(e) => e.stopPropagation()}
              onSubmit={handleSubmit(onSubmit)}
            >
              <SheetHeader>
                <SheetTitle>{edit ? 'Edit' : 'Create'} User</SheetTitle>
                <SheetDescription>{edit ? data?.name : ''}</SheetDescription>
              </SheetHeader>
              <SheetBody>
                <div className="max-w-sm space-y-2">
                  <FormField
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Name</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            {...field}
                            placeholder="e.g. Jane Smith"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            {...field}
                            placeholder="e.g. jane.smith@landmarkhospitality.com"
                            autoComplete="new-email"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <FormItem>
                        <Tooltip delayDuration={500}>
                          <TooltipTrigger asChild>
                            <div className="flex w-fit gap-1">
                              <FormLabel>Password</FormLabel>
                              <FontAwesomeIcon
                                icon={faCircleInfo}
                                className="h-4 w-4"
                              />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="left">
                            <div className="flex flex-col">
                              {DEFAULT_PASSWORD_TOOL_TIP.map((string, i) => {
                                return (
                                  <span key={`${string}${i}`}>{string}</span>
                                );
                              })}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                        <FormControl>
                          <Input
                            type="password"
                            {...field}
                            placeholder="Enter a password"
                            autoComplete="new-password"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    name="phoneNumber"
                    control={control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone Number</FormLabel>
                        <FormControl>
                          <Input
                            type="tel"
                            value={field.value || ''}
                            maxLength={14}
                            placeholder="(555) 555-5555"
                            autoComplete="tel"
                            inputMode="numeric"
                            onChange={(e) => {
                              const formatted = formatPhoneNumber(
                                e.target.value,
                              );
                              field.onChange(formatted);
                            }}
                            onKeyDown={(e) => {
                              if (
                                !/[0-9]|Backspace|Tab|ArrowLeft|ArrowRight/.test(
                                  e.key,
                                )
                              ) {
                                e.preventDefault();
                              }
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    name="roles"
                    control={control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Roles</FormLabel>
                        <FormControl>
                          <AsyncSelect
                            isMulti
                            loadOptions={loadOptions}
                            value={field.value}
                            onChange={(option) => field.onChange(option)}
                            placeholder="Search..."
                            classNamePrefix="select"
                            styles={customStyles}
                            noOptionsMessage={() => 'No results found'}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </SheetBody>
              <SheetFooter>
                <Button type="submit">{edit ? 'Save' : 'Submit'}</Button>
              </SheetFooter>
            </form>
          </Form>
        </SheetContent>
      </Sheet>
    </div>
  );
}

export default UserForm;
